import { hsReadStorageQuota } from '../../../shared/streaming/storage-quota.function';
import { hsIsConstrainedDevice } from '../../../shared/streaming/tile-cache-eviction.function';

export type HsGeometryPayloadKind = 'meshes' | 'lines' | 'pointClouds';

export interface HsGeometryPayloadKey {
  kind: HsGeometryPayloadKind;
  geometryIds: readonly string[];
  sceneId: string;
  modelId: string;
  /** Payload format (`binary`, `draco`); part of the key so formats never mix. */
  format: string;
  /** Bumped when the wire layout changes, so older entries stop matching. */
  protocolVersion: number;
  canonicalGeometryIds?: readonly string[];
}

/** Order-independent cache key for one geometry download. */
export function hsMakeGeometryPayloadKey(key: HsGeometryPayloadKey): string {
  const byText = (left: string, right: string): number => left.localeCompare(right);
  const ids: string[] = [...key.geometryIds].sort(byText);
  const canonical: string[] = [...(key.canonicalGeometryIds ?? [])].sort(byText);
  return `${key.sceneId}|${key.modelId}|v${key.protocolVersion}|${key.format}|${key.kind}:${ids.join(
    ','
  )}|canonical:${canonical.join(',')}`;
}

export interface HsGeometryPayloadCacheOptions {
  databaseName: string;
  storeName?: string;
  /** Entry cap on top of the byte budget; none by default. The least recently written entries go first. */
  maxEntries?: number;
  /** Byte budget; default `hsDefaultGeometryPayloadCacheBytes()`, at most half of the storage quota. */
  maxBytes?: number;
  indexedDb?: IDBFactory;
}

export const HS_DESKTOP_GEOMETRY_PAYLOAD_CACHE_BYTES: number = 3 * 1024 * 1024 * 1024;
export const HS_MOBILE_GEOMETRY_PAYLOAD_CACHE_BYTES: number = 1024 * 1024 * 1024;

/** Default byte budget of the geometry payload cache: 3 GiB, 1 GiB on phones, tablets and low-memory devices. */
export function hsDefaultGeometryPayloadCacheBytes(): number {
  return hsIsConstrainedDevice() ? HS_MOBILE_GEOMETRY_PAYLOAD_CACHE_BYTES : HS_DESKTOP_GEOMETRY_PAYLOAD_CACHE_BYTES;
}

/** Index over the fields the prune needs, so it reads keys only and never deserializes payloads. */
const PRUNE_INDEX: string = 'prune';
const PRUNE_INDEX_KEY_PATH: string[] = ['sceneId', 'modelId', 'touchedAt', 'bytes'];
/** Version 2 added the prune index; it drops version-1 stores, whose entries had no fixed shape. */
const SCHEMA_VERSION: number = 2;

interface PrunableEntry {
  key: IDBValidKey;
  sceneId: string;
  modelId: string;
  touchedAt: number;
  bytes: number;
}

interface CacheEntry {
  payload: ArrayBuffer;
  kind: HsGeometryPayloadKind;
  sceneId: string;
  modelId: string;
  bytes: number;
  touchedAt: number;
}

/** What the cache holds: the entry count and the payload bytes, summed from the prune index. */
export interface HsGeometryPayloadCacheUsage {
  entries: number;
  bytes: number;
}

/**
 * Keeps downloaded mesh and line payloads in IndexedDB so reopening a scene skips the network. Entries of every
 * scene share the budget and are keyed by scene and model, so switching between scenes keeps both cached; the
 * least recently written entries go first when the budget is exceeded.
 */
export class HsGeometryPayloadCache {
  private readonly storeName: string;
  private readonly maxEntries: number;
  private maxBytes: number;
  private quotaChecked: boolean;
  private database: Promise<IDBDatabase> | null = null;

  public constructor(private readonly options: HsGeometryPayloadCacheOptions) {
    this.storeName = options.storeName ?? 'geometry-binary';
    this.maxEntries = options.maxEntries ?? Number.POSITIVE_INFINITY;
    this.maxBytes = options.maxBytes ?? hsDefaultGeometryPayloadCacheBytes();
    // A host-given budget is kept as is; the default follows the browser's quota.
    this.quotaChecked = options.maxBytes !== undefined;
  }

  /** The byte budget in force: the default or the host's, reduced to half of the storage quota once known. */
  public get budgetBytes(): number {
    return this.maxBytes;
  }

  /** The cached payload, or null when it is missing or the store cannot be read. */
  public async read(key: string): Promise<ArrayBuffer | null> {
    try {
      const db: IDBDatabase = await this.open();
      const result: unknown = await request(
        db.transaction(this.storeName, 'readonly').objectStore(this.storeName).get(key)
      );
      if (typeof result !== 'object' || result === null) {
        return null;
      }
      const entry: Partial<CacheEntry> = result as Partial<CacheEntry>;
      return entry.payload instanceof ArrayBuffer ? entry.payload : null;
    } catch {
      return null;
    }
  }

  /** Stores a payload and prunes the store; rejects when IndexedDB is unavailable. */
  public async write(
    key: string,
    context: { kind: HsGeometryPayloadKind; sceneId: string; modelId: string },
    payload: ArrayBuffer
  ): Promise<void> {
    this.clampToQuota();
    const db: IDBDatabase = await this.open();
    const transaction: IDBTransaction = db.transaction(this.storeName, 'readwrite');
    const entry: CacheEntry = { payload, ...context, bytes: payload.byteLength, touchedAt: Date.now() };
    transaction.objectStore(this.storeName).put(entry, key);
    await complete(transaction);
    await this.prune(db);
  }

  public async clear(): Promise<void> {
    const db: IDBDatabase = await this.open();
    const transaction: IDBTransaction = db.transaction(this.storeName, 'readwrite');
    transaction.objectStore(this.storeName).clear();
    await complete(transaction);
  }

  /** Entries and bytes in the store, read from the prune index without loading payloads. */
  public async usage(): Promise<HsGeometryPayloadCacheUsage> {
    const entries: PrunableEntry[] = await this.scan(await this.open());
    return {
      entries: entries.length,
      bytes: entries.reduce((sum: number, item: PrunableEntry) => sum + item.bytes, 0),
    };
  }

  /**
   * Every entry's index key (scene, model, time, bytes) and primary key, through a key cursor over the prune index,
   * so neither the prune nor `usage` loads a payload.
   */
  private async scan(db: IDBDatabase): Promise<PrunableEntry[]> {
    const entries: PrunableEntry[] = [];
    const cursorRequest: IDBRequest<IDBCursor | null> = db
      .transaction(this.storeName, 'readonly')
      .objectStore(this.storeName)
      .index(PRUNE_INDEX)
      .openKeyCursor();
    await new Promise<void>((resolve: () => void, reject: (reason?: unknown) => void) => {
      cursorRequest.onsuccess = () => {
        const cursor: IDBCursor | null = cursorRequest.result;
        if (cursor === null) {
          resolve();
          return;
        }
        const entry: PrunableEntry | null = toPrunable(cursor.key, cursor.primaryKey);
        if (entry !== null) {
          entries.push(entry);
        }
        cursor.continue();
      };
      cursorRequest.onerror = () => reject(cursorRequest.error ?? new Error('Failed to read cache entries.'));
    });
    return entries;
  }

  private clampToQuota(): void {
    if (this.quotaChecked) {
      return;
    }
    this.quotaChecked = true;
    void hsReadStorageQuota().then((quota: number | null) => {
      if (quota !== null) {
        this.maxBytes = Math.min(this.maxBytes, Math.floor(quota / 2));
      }
    });
  }

  private async prune(db: IDBDatabase): Promise<void> {
    const entries: PrunableEntry[] = await this.scan(db);
    entries.sort((left: PrunableEntry, right: PrunableEntry) => left.touchedAt - right.touchedAt);
    let totalBytes: number = entries.reduce((sum: number, item: PrunableEntry) => sum + item.bytes, 0);
    const doomed: IDBValidKey[] = [];
    for (const item of entries) {
      if (entries.length - doomed.length <= this.maxEntries && totalBytes <= this.maxBytes) {
        break;
      }
      doomed.push(item.key);
      totalBytes -= item.bytes;
    }
    if (doomed.length === 0) {
      return;
    }
    const transaction: IDBTransaction = db.transaction(this.storeName, 'readwrite');
    const store: IDBObjectStore = transaction.objectStore(this.storeName);
    doomed.forEach((key: IDBValidKey) => store.delete(key));
    await complete(transaction);
  }

  private open(): Promise<IDBDatabase> {
    this.database ??= new Promise<IDBDatabase>(
      (resolve: (db: IDBDatabase) => void, reject: (reason: unknown) => void) => {
        const factory: IDBFactory | undefined = this.options.indexedDb ?? globalThis.indexedDB;
        if (factory === undefined) {
          reject(new Error('IndexedDB is not available.'));
          return;
        }
        const openRequest: IDBOpenDBRequest = factory.open(this.options.databaseName, SCHEMA_VERSION);
        openRequest.onupgradeneeded = () => {
          const db: IDBDatabase = openRequest.result;
          if (db.objectStoreNames.contains(this.storeName)) {
            db.deleteObjectStore(this.storeName);
          }
          db.createObjectStore(this.storeName).createIndex(PRUNE_INDEX, PRUNE_INDEX_KEY_PATH);
        };
        openRequest.onsuccess = () => {
          const db: IDBDatabase = openRequest.result;
          // Another tab opened a newer schema: close so its upgrade is not blocked; the next call reopens.
          db.onversionchange = () => {
            db.close();
            this.database = null;
          };
          resolve(db);
        };
        openRequest.onerror = () => reject(openRequest.error ?? new Error('Failed to open IndexedDB.'));
        openRequest.onblocked = () => reject(new Error('IndexedDB upgrade is blocked by another tab.'));
      }
    );
    return this.database;
  }
}

function toPrunable(indexKey: IDBValidKey, primaryKey: IDBValidKey): PrunableEntry | null {
  if (!Array.isArray(indexKey) || indexKey.length !== PRUNE_INDEX_KEY_PATH.length) {
    return null;
  }
  const [sceneId, modelId, touchedAt, bytes]: unknown[] = indexKey;
  if (
    typeof sceneId !== 'string' ||
    typeof modelId !== 'string' ||
    typeof touchedAt !== 'number' ||
    typeof bytes !== 'number'
  ) {
    return null;
  }
  return { key: primaryKey, sceneId, modelId, touchedAt, bytes };
}

function request<T>(idbRequest: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve: (value: T) => void, reject: (reason: unknown) => void) => {
    idbRequest.onsuccess = () => resolve(idbRequest.result);
    idbRequest.onerror = () => reject(idbRequest.error ?? new Error('IndexedDB request failed.'));
  });
}

function complete(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve: () => void, reject: (reason: unknown) => void) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted.'));
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed.'));
  });
}
